<template>
  <modal :show="showModal" modal-classes="modal-secondary" size="lg">
    <div class="row">
      <div class="col-12">
        <base-input :label="`${$t('COMMON.DESTINATION')} *`">
          <establishment-selector
            :onlyActive="true"
            :allowNone="false"
            :establishment="destinationEstablishmentId"
            :filterOrganization="establishment.organization && establishment.organization.id"
            :bypassScope="true"
            @establishmentChanged="onDestinationChanged"
          />
        </base-input>
        <validation-error
          :errors="apiValidationErrors.destinationEstablishment"
        />
      </div>

      <div class="col-12">
        <base-input :label="`${$t('EQUIPMENT_TRANSFERS.TRANSFER_TYPE')} *`">
          <el-radio-group v-model="transferType">
            <el-radio :label="TYPE_TEMPORARY">
              {{ $t("EQUIPMENT_TRANSFERS.TYPE_TEMPORARY") }}
            </el-radio>
            <el-radio :label="TYPE_PERMANENT">
              {{ $t("EQUIPMENT_TRANSFERS.TYPE_PERMANENT") }}
            </el-radio>
          </el-radio-group>
        </base-input>
        <validation-error :errors="apiValidationErrors.transfer_type" />
      </div>

      <div class="col-12">
        <base-input :label="`${$t('EQUIPMENT_TRANSFERS.TRANSFER_DATE')} *`">
          <flat-picker
            v-model="transferDate"
            :config="{
              allowInput: true,
              locale: $flatPickrLocale(),
            }"
            class="form-control datepicker"
          />
        </base-input>
        <validation-error :errors="apiValidationErrors.transfer_date" />
      </div>

      <div class="col-12" v-if="transferType === TYPE_TEMPORARY">
        <base-input :label="`${$t('EQUIPMENT_TRANSFERS.RETURN_DATE')} *`">
          <flat-picker
            v-model="returnDate"
            :config="{
              allowInput: true,
              locale: $flatPickrLocale(),
            }"
            class="form-control datepicker"
          />
        </base-input>
        <validation-error :errors="apiValidationErrors.return_date" />
      </div>

      <div class="col-12">
        <base-input :label="$t('COMMON.NOTE')" :placeholder="$t('COMMON.NOTE')">
          <textarea class="form-control" rows="3" v-model="excerpt"></textarea>
        </base-input>
        <validation-error :errors="apiValidationErrors.excerpt" />
      </div>
    </div>

    <template slot="footer">
      <button
        type="submit"
        class="btn btn-primary"
        @click="createTransfer"
        :disabled="
          loading ||
          !destinationEstablishmentId ||
          (transferType === TYPE_TEMPORARY && !returnDate)
        "
      >
        <span v-if="loading" class="btn-inner--icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        {{ $t("COMMON.SAVE") }}
      </button>

      <button
        type="button"
        class="btn btn-link ml-auto"
        @click="closeModal"
        :disabled="loading"
      >
        {{ $t("COMMON.CLOSE") }}
      </button>
    </template>
  </modal>
</template>

<script>
import { cloneDeep } from "lodash";
import moment from "moment";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { Radio, RadioGroup } from "element-ui";
import formMixin from "@/mixins/form-mixin";
import ValidationError from "@/components/ValidationError.vue";
import EstablishmentSelector from "@/components/EstablishmentSelector.vue";
import defaultEquipmentTransfer from "../../../InventoryModule/EquipmentTransferManagement/defaultEquipmentTransfer";
import {
  EQUIPMENT_TRANSFER_TYPE_TEMPORARY,
  EQUIPMENT_TRANSFER_TYPE_PERMANENT,
} from "@/constants/equipmentTransfers";

export default {
  name: "EstablishmentViewEquipmentTransfersAddModal",
  components: {
    EstablishmentSelector,
    ValidationError,
    flatPicker,
    [Radio.name]: Radio,
    [RadioGroup.name]: RadioGroup,
  },
  mixins: [formMixin],
  props: {
    establishment: {
      type: Object,
      required: true,
    },
    showModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      destinationEstablishmentId: null,
      transferType: EQUIPMENT_TRANSFER_TYPE_PERMANENT,
      transferDate: moment().format("YYYY-MM-DD"),
      returnDate: null,
      excerpt: "",
      TYPE_TEMPORARY: EQUIPMENT_TRANSFER_TYPE_TEMPORARY,
      TYPE_PERMANENT: EQUIPMENT_TRANSFER_TYPE_PERMANENT,
    };
  },
  watch: {
    showModal(value) {
      if (value) {
        this.transferDate = moment().format("YYYY-MM-DD");
      } else {
        this.resetForm();
      }
    },
    establishment: {
      deep: true,
      handler() {
        this.resetForm();
      },
    },
  },
  methods: {
    onDestinationChanged(establishmentId) {
      this.destinationEstablishmentId = establishmentId;
      this.resetApiValidation();
    },
    resetForm() {
      this.loading = false;
      this.destinationEstablishmentId = null;
      this.transferType = EQUIPMENT_TRANSFER_TYPE_PERMANENT;
      this.transferDate = moment().format("YYYY-MM-DD");
      this.returnDate = null;
      this.excerpt = "";
      this.resetApiValidation();
    },
    closeModal() {
      this.resetForm();
      this.$emit("onCloseModal");
    },
    async createTransfer() {
      if (!this.establishment?.id || !this.destinationEstablishmentId) {
        return;
      }
      if (this.transferType === this.TYPE_TEMPORARY && !this.returnDate) {
        return;
      }
      this.loading = true;
      const payload = cloneDeep(defaultEquipmentTransfer);
      payload.transfer_date = this.transferDate;
      payload.transfer_type = this.transferType;
      payload.return_date =
        this.transferType === this.TYPE_TEMPORARY ? this.returnDate : null;
      payload.excerpt = this.excerpt;
      payload.organization = {
        type: "organizations",
        id: this.establishment.organization?.id,
      };
      payload.sourceEstablishment = {
        type: "establishments",
        id: this.establishment.id,
      };
      payload.destinationEstablishment = {
        type: "establishments",
        id: this.destinationEstablishmentId,
      };
      if (this.establishment.serviceCenter?.id) {
        payload.serviceCenter = {
          type: "service-centers",
          id: this.establishment.serviceCenter.id,
        };
      }

      try {
        await this.$store.dispatch("equipmentTransfers/add", {
          data: payload,
        });
        const transfer = this.$store.getters["equipmentTransfers/transfer"];
        this.$notify({
          type: "success",
          message: this.$t("COMMON.SAVED"),
        });
        this.$emit("created", transfer);
        this.closeModal();
        this.$router.push({
          name: "View EquipmentTransfer",
          params: { id: transfer.id },
        });
      } catch (error) {
        if (error?.response?.data?.errors) {
          this.setApiValidation(error.response.data.errors);
        }
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
